import type { VaultCoreParams, VaultRiskParams } from 'src/pages/admin/services/vaultParamsService';

import { VAULT_PARAMS_NS as NS } from './constants';

export type FieldKind =
  | 'usdc'
  | 'days'
  | 'percent'
  | 'multiplier'
  | 'seconds'
  | 'milliseconds'
  | 'count'
  | 'symbols';

export interface FieldSpec<T> {
  key: keyof T & string;
  kind: FieldKind;
  /** i18n key */
  label: string;
  /** i18n key, shown under the input */
  hint?: string;
  /** i18n key for the range text, rendered via `${NS}.allowedRange` alongside
   * the hint/preview and next to any error. */
  range?: string;
  /** i18n key of the section this field is shown under. Consecutive fields with the
   * same group render under one heading (PO request, QA 2026-09-29 item 7); fields
   * without one render flat. Presentation only — the payload is unchanged. */
  group?: string;
  /** True when a DEPLOYED, non-test engine reader acts on this field: refuses, checks,
   * stamps or alerts on it. The read view flags every other field with a "stored — not
   * yet enforced" chip. A field whose reader only alerts says so in its hint (the risk
   * monitor's four). Each field's comment names its reader in the engine repo; flip a
   * field to true only when such a reader ships. */
  enforced: boolean;
  /** Shown in the read view only: the edit dialog does not list it, and neither the draft,
   * the confirm diff nor the save payload carries it. For a value the engine fixes. */
  readOnly?: boolean;
}

export const CORE_FIELDS: FieldSpec<VaultCoreParams>[] = [
  {
    // Read by vault_api --ledger when a subscription is quoted and submitted
    // (services/vaultread/ledger_quote.go, services/vaultwrite/subscribe.go in the engine
    // repo), and as the floor of a community application's stake.
    key: 'minSubscription',
    kind: 'usdc',
    label: `${NS}.fields.minSubscription`,
    range: `${NS}.ranges.minSubscription`,
    enforced: true,
  },
  {
    // Read by vault_api --ledger when a redemption is quoted and submitted
    // (services/vaultread/ledger_quote.go, services/vaultwrite/redeem.go).
    key: 'minRedemption',
    kind: 'usdc',
    label: `${NS}.fields.minRedemption`,
    hint: `${NS}.hints.minRedemption`,
    range: `${NS}.ranges.minRedemption`,
    enforced: true,
  },
  // The next four are checked by admin_gql requestOfficialVault when an official vault is
  // requested (XBIT-4078, C-06 §3.1; admin/internal/onboarding/service.go in the engine
  // repo); existing vaults keep their terms.
  {
    key: 'lockupMinDays',
    kind: 'days',
    label: `${NS}.fields.lockupMinDays`,
    hint: `${NS}.hints.newOfficialVaults`,
    range: `${NS}.ranges.lockupMinDays`,
    enforced: true,
  },
  {
    key: 'lockupMaxDays',
    kind: 'days',
    label: `${NS}.fields.lockupMaxDays`,
    hint: `${NS}.hints.newOfficialVaults`,
    range: `${NS}.ranges.lockupMaxDays`,
    enforced: true,
  },
  {
    // UI copy is the PO's label from Module PRD §14.7 ("profit-share cap"); the
    // identifier keeps the ubiquitous-language name. UL: only UI copy may differ.
    key: 'performanceFeeCap',
    kind: 'percent',
    label: `${NS}.fields.performanceFeeCap`,
    hint: `${NS}.hints.newOfficialVaults`,
    range: `${NS}.ranges.performanceFeeCap`,
    enforced: true,
  },
  {
    key: 'ownershipFloor',
    kind: 'percent',
    label: `${NS}.fields.ownershipFloor`,
    hint: `${NS}.hints.newOfficialVaults`,
    range: `${NS}.ranges.ownershipFloor`,
    enforced: true,
  },
  {
    // Fixed at 1 in V1 (C-03 §3.24): the engine refuses any other value, so it is shown and never sent.
    key: 'maxApplicationsInFlight',
    kind: 'count',
    label: `${NS}.fields.maxApplicationsInFlight`,
    enforced: true,
    readOnly: true,
  },
  {
    // Read by the community intake at createVault (XBIT-4659): cmd/vault_api/intake.go and
    // services/vaultintake/intake.go in the engine repo.
    key: 'maxLiveVaultsPerManager',
    kind: 'count',
    label: `${NS}.fields.maxLiveVaultsPerManager`,
    hint: `${NS}.hints.maxLiveVaultsPerManager`,
    range: `${NS}.ranges.maxLiveVaultsPerManager`,
    enforced: true,
  },
];

// The three sections the PO asked for (QA 2026-09-29 item 7): trading risk,
// redemption risk, venue clock / NAV fence. Same page, same payload — only headings.
const TRADING = `${NS}.risk.groups.trading`;
const REDEMPTION = `${NS}.risk.groups.redemption`;
const VENUE_CLOCK = `${NS}.risk.groups.venueClock`;

export const RISK_FIELDS: FieldSpec<VaultRiskParams>[] = [
  {
    // The close engine's order gateway refuses an opening order on a coin outside it
    // (services/quantgateway/gateway.go in the engine repo); the after-fill risk
    // monitor alerts on a position outside it (services/vaultrisk/evaluate.go).
    key: 'instrumentWhitelist',
    kind: 'symbols',
    label: `${NS}.fields.instrumentWhitelist`,
    hint: `${NS}.hints.instrumentWhitelist`,
    group: TRADING,
    enforced: true,
  },
  // The next four are read by the after-fill risk monitor in vault_api --ledger
  // (services/vaultrisk/evaluate.go, XBIT-4741). It alerts on a breach and never
  // refuses or reduces an order, which their shared hint says.
  {
    key: 'maxLeverage',
    kind: 'multiplier',
    label: `${NS}.fields.maxLeverage`,
    hint: `${NS}.hints.riskMonitor`,
    range: `${NS}.ranges.maxLeverage`,
    group: TRADING,
    enforced: true,
  },
  {
    key: 'concentrationCap',
    kind: 'percent',
    label: `${NS}.fields.concentrationCap`,
    hint: `${NS}.hints.riskMonitor`,
    range: `${NS}.ranges.concentrationCap`,
    group: TRADING,
    enforced: true,
  },
  {
    key: 'marginWarning',
    kind: 'percent',
    label: `${NS}.fields.marginWarning`,
    hint: `${NS}.hints.riskMonitor`,
    range: `${NS}.ranges.marginWarning`,
    group: TRADING,
    enforced: true,
  },
  {
    key: 'forcedDeleverage',
    kind: 'percent',
    label: `${NS}.fields.forcedDeleverage`,
    hint: `${NS}.hints.riskMonitor`,
    range: `${NS}.ranges.forcedDeleverage`,
    group: TRADING,
    enforced: true,
  },
  {
    // vault_api --ledger stamps it on the closure a redemption opens
    // (services/vaultwrite/redeem.go in the engine repo), whose verdict judges the
    // shortfall against it; the close engine in vault_treasury_executor reads it too
    // (services/quantclose/engine.go).
    key: 'slippageTolerance',
    kind: 'percent',
    label: `${NS}.fields.slippageTolerance`,
    hint: `${NS}.hints.slippageTolerance`,
    range: `${NS}.ranges.slippageTolerance`,
    group: REDEMPTION,
    enforced: true,
  },
  {
    // No engine code reads it outside tests and validation: the one stored-only field.
    key: 'closeTimeoutSeconds',
    kind: 'seconds',
    label: `${NS}.fields.closeTimeoutSeconds`,
    range: `${NS}.ranges.closeTimeoutSeconds`,
    group: REDEMPTION,
    enforced: false,
  },
  {
    // vault_api --ledger asks the manager to confirm a position close above it
    // (services/vaultread/close_preview.go) and tells the holders after one
    // (services/closenotice/runner.go).
    key: 'confirmCloseThreshold',
    kind: 'percent',
    label: `${NS}.fields.confirmCloseThreshold`,
    hint: `${NS}.hints.confirmCloseThreshold`,
    range: `${NS}.ranges.confirmCloseThreshold`,
    group: REDEMPTION,
    enforced: true,
  },
  // Venue-clock thresholds. Enforced: read by vault_engine's netvalue fence since
  // XBIT-4165. They share one hint (the group's) since each is only meaningful
  // alongside the others.
  {
    key: 'eventTimeToleranceMs',
    kind: 'milliseconds',
    label: `${NS}.fields.eventTimeToleranceMs`,
    hint: `${NS}.hints.venueClock`,
    range: `${NS}.ranges.eventTimeToleranceMs`,
    group: VENUE_CLOCK,
    enforced: true,
  },
  {
    key: 'venueLagAllowanceMs',
    kind: 'milliseconds',
    label: `${NS}.fields.venueLagAllowanceMs`,
    hint: `${NS}.hints.venueClock`,
    range: `${NS}.ranges.venueLagAllowanceMs`,
    group: VENUE_CLOCK,
    enforced: true,
  },
  {
    key: 'maxVenueReadSpanMs',
    kind: 'milliseconds',
    label: `${NS}.fields.maxVenueReadSpanMs`,
    hint: `${NS}.hints.venueClock`,
    range: `${NS}.ranges.maxVenueReadSpanMs`,
    group: VENUE_CLOCK,
    enforced: true,
  },
  {
    key: 'venueTimeSkewBandMs',
    kind: 'milliseconds',
    label: `${NS}.fields.venueTimeSkewBandMs`,
    hint: `${NS}.hints.venueClock`,
    range: `${NS}.ranges.venueTimeSkewBandMs`,
    group: VENUE_CLOCK,
    enforced: true,
  },
];
